import { Brain, Plus, Trash2, User, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { getDomainInfo, getReactIcon } from '../../hooks_suggestion/useDomainSuggestions';

export const DomainSection = ({
    categories,
    userAssessment,
    onOpenSkillsModal,
    customSkillDomains = [],
    selectedDomain,
    onDomainSelect,
    onDeleteDomain,
    domainesData = [],
    skillsData = [],
    refreshTrigger,
    onRefreshPage
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    const [selectedDomainsForDeletion, setSelectedDomainsForDeletion] = useState(new Set());
    const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

    const skillsByDomain = useMemo(() => {
        const formatted = {};

        skillsData.forEach(skill => {
            const domainId = skill.id_domaine;
            if (!formatted[domainId]) {
                formatted[domainId] = [];
            }
            formatted[domainId].push(skill);
        });

        return formatted;
    }, [skillsData, refreshTrigger]);

    const getCustomProgressColor = (score) => {
        if (score <= 40) {
            return '#e57a86';
        } else if (score <= 60) {
            return '#ffad65';
        } else {
            return '#0e9488';
        }
    };

    const calculateDomainScore = useCallback((domainId) => {
        const domainSkills = skillsByDomain[domainId] || [];

        if (domainSkills.length === 0) {
            return 0;
        }

        const totalRatingSum = domainSkills.reduce((sum, skill) => {
            return sum + (skill.rating || 0);
        }, 0);

        const maxPossibleScore = domainSkills.length * 5;
        const score = maxPossibleScore > 0 ? Math.round((totalRatingSum / maxPossibleScore) * 100) : 0;

        return score;
    }, [skillsByDomain]);

    const combinedDomains = useMemo(() => {
        const dbDomains = domainesData.map(domaine => ({
            id: domaine.id_domaine,
            name: domaine.nom_domaine,
            icon: domaine.icon,
            description: domaine.description,
            score: calculateDomainScore(domaine.id_domaine),
            level: domaine.niveau_evaluation || 'Débutant',
            type_domaine: domaine.type_domaine,
            isCustom: domaine.type_domaine === 'custom'
        }));

        const legacyCategories = categories.filter(cat =>
            !dbDomains.some(dbDomain =>
                dbDomain.name.toLowerCase() === cat.name.toLowerCase()
            )
        ).map(cat => ({
            id: cat.id,
            name: cat.name,
            icon: cat.icon,
            description: cat.description,
            score: userAssessment[cat.id]?.score || 0,
            level: userAssessment[cat.id]?.level || 'Débutant',
            isCustom: false
        }));

        const legacyCustomCategories = customSkillDomains.filter(domain =>
            !dbDomains.some(dbDomain =>
                dbDomain.name.toLowerCase() === domain.toLowerCase()
            ) && !legacyCategories.some(legacyCat =>
                legacyCat.name.toLowerCase() === domain.toLowerCase()
            )
        ).map((domain, index) => ({
            id: `custom-${index}-${domain}`,
            name: domain,
            isCustom: true,
            index,
            score: 0,
            level: 'Débutant'
        }));

        return [...dbDomains, ...legacyCategories, ...legacyCustomCategories];
    }, [domainesData, categories, userAssessment, customSkillDomains, calculateDomainScore, refreshTrigger]);

    const hasAnyDomain = combinedDomains.length > 0;

    const paginatedDomains = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return combinedDomains.slice(startIndex, endIndex);
    }, [combinedDomains, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(combinedDomains.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const goToPrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handleDelete = useCallback(async (domainId) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ce domaine ? Cette action est irréversible.`)) {
            return;
        }

        try {
            if (onDeleteDomain) {
                await onDeleteDomain(domainId);
            }

            if (onRefreshPage) {
                await onRefreshPage(true);
            }

            if (paginatedDomains.length === 1 && currentPage > 1 && totalPages > 1) {
                setCurrentPage(prevPage => prevPage - 1);
            }
        } catch (error) {
            //
        }
    }, [onDeleteDomain, onRefreshPage, paginatedDomains.length, currentPage, totalPages]);

    const handleMultipleDelete = useCallback(async () => {
        const selectedCount = selectedDomainsForDeletion.size;
        if (selectedCount === 0) return;

        const confirmMessage = selectedCount === 1 
            ? `Êtes-vous sûr de vouloir supprimer ce domaine ? Cette action est irréversible.`
            : `Êtes-vous sûr de vouloir supprimer ces ${selectedCount} domaines ? Cette action est irréversible.`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            if (onDeleteDomain) {
                // Supprimer tous les domaines sélectionnés
                for (const domainId of selectedDomainsForDeletion) {
                    await onDeleteDomain(domainId);
                }
            }

            // Réinitialiser la sélection
            setSelectedDomainsForDeletion(new Set());
            setIsMultiSelectMode(false);

            if (onRefreshPage) {
                await onRefreshPage(true);
            }

            // Ajuster la page si nécessaire
            if (paginatedDomains.length <= selectedCount && currentPage > 1 && totalPages > 1) {
                setCurrentPage(prevPage => Math.max(1, prevPage - 1));
            }
        } catch (error) {
            console.error('Erreur lors de la suppression multiple:', error);
        }
    }, [selectedDomainsForDeletion, onDeleteDomain, onRefreshPage, paginatedDomains.length, currentPage, totalPages]);

    const toggleDomainSelection = useCallback((domainId) => {
        setSelectedDomainsForDeletion(prev => {
            const newSet = new Set(prev);
            if (newSet.has(domainId)) {
                newSet.delete(domainId);
            } else {
                newSet.add(domainId);
            }
            return newSet;
        });
    }, []);

    const toggleMultiSelectMode = useCallback(() => {
        setIsMultiSelectMode(prev => !prev);
        if (isMultiSelectMode) {
            setSelectedDomainsForDeletion(new Set());
        }
    }, [isMultiSelectMode]);

    const handleOpenSkillsModal = useCallback(() => {
        if (onOpenSkillsModal) {
            onOpenSkillsModal();
        }
    }, [onOpenSkillsModal]);

    const PaginationControls = () => (
        <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
            <button
                onClick={goToPrevPage}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Précédent
            </button>

            <div className="flex items-center space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                    <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${currentPage === pageNumber
                            ? 'bg-[#d9efeb] text-gray-800'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                    >
                        {pageNumber}
                    </button>
                ))}
            </div>

            <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Suivant
            </button>
        </div>
    );

    const isDomainDeletable = (domain) => {
        return !!domain.id;
    };

    const renderDomain = (domain) => {
        const isSelected = selectedDomain === domain.id;
        const isSelectedForDeletion = selectedDomainsForDeletion.has(domain.id);
        const domainInfo = getDomainInfo(domain.name || domain.id);

        const displayName = domainInfo?.nom || domain.name;
        const displayIcon = domainInfo?.icon || domain.icon;
        const displayDescription = domainInfo?.description || domain.description;
        const score = domain.score || 0;
        const level = domain.level || 'Débutant';

        const IconComponent = displayIcon ? getReactIcon(displayIcon) : null;

        const domainSkills = skillsByDomain[domain.id] || [];
        const skillsWithRating = domainSkills.filter(skill => skill.rating && skill.rating > 0);
        const totalRating = skillsWithRating.reduce((sum, skill) => sum + skill.rating, 0);
        const maxPossible = domainSkills.length * 5;

        return (
            <div
                key={domain.id}
                className={`group flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all duration-200 min-w-0 ${
                    isSelectedForDeletion
                        ? 'bg-red-100 border-2 border-red-300'
                        : isSelected
                        ? 'bg-[#c8d7d6] text-gray-800'
                        : 'bg-gray-50 hover:bg-gray-100 hover:shadow-md'
                }`}
                title={`${displayName}: ${score}% (${totalRating}/${maxPossible} points)`}
            >
                {/* Checkbox pour la sélection multiple */}
                {isMultiSelectMode && isDomainDeletable(domain) && (
                    <div className="flex items-center mr-3">
                        <input
                            type="checkbox"
                            checked={isSelectedForDeletion}
                            onChange={() => toggleDomainSelection(domain.id)}
                            className="w-4 h-4 accent-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                )}

                <div className="flex items-center">
                    {IconComponent ? (
                        <>
                            <div className="flex items-center justify-center w-8 h-8">
                                <IconComponent className={`w-4 h-4 ${isSelected ? 'text-gray-600' : 'text-gray-500'}`} />
                            </div>
                            <div className={`w-px h-8 mx-3 ${isSelected ? 'bg-gray-400' : 'bg-gray-300'}`}></div>
                        </>
                    ) : displayIcon ? (
                        <>
                            <div className="flex items-center justify-center w-8 h-8">
                                <span className="text-lg">
                                    {displayIcon}
                                </span>
                            </div>
                            <div className={`w-px h-8 mx-3 ${isSelected ? 'bg-gray-400' : 'bg-gray-300'}`}></div>
                        </>
                    ) : (
                        <>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isSelected ? 'bg-gray-200' : 'bg-gray-100'
                                }`}>
                                <User className={`w-3 h-3 ${isSelected ? 'text-gray-600' : 'text-[#d9efeb]'}`} />
                            </div>
                            <div className={`w-px h-8 mx-3 ${isSelected ? 'bg-gray-400' : 'bg-gray-300'}`}></div>
                        </>
                    )}
                </div>

                <div
                    className="flex items-center space-x-2 min-w-0 flex-1"
                    onClick={() => !isMultiSelectMode && onDomainSelect(domain.id)}
                >
                    <div className="min-w-0 flex-1">
                        <div className={`font-medium truncate ${isSelected ? 'text-gray-800' : 'text-gray-900'}`}>
                            {displayName}
                        </div>
                        <div className={`text-xs truncate mt-1 ${isSelected ? 'text-gray-600' : 'text-gray-500'}`}>
                            {displayDescription || (domain.isCustom ? 'Domaine personnalisé' :
                                domainSkills.length > 0 ?
                                    `${skillsWithRating.length}/${domainSkills.length} compétences évaluées` :
                                    `Niveau: ${level}`)}
                        </div>
                    </div>
                </div>

                <div className="flex items-center space-x-2 flex-shrink-0">
                    <div className={`w-16 h-2 rounded-full ${isSelected ? 'bg-gray-300' : 'bg-gray-200'}`}>
                        <div
                            className="h-2 rounded-full"
                            style={{
                                width: `${Math.min(score, 100)}%`,
                                backgroundColor: isSelected ? '#6b7280' : getCustomProgressColor(score)
                            }}
                        />
                    </div>
                    <span className={`text-sm font-bold ${isSelected ? 'text-gray-800' : 'text-gray-900'}`}>
                        {score}%
                    </span>
                </div>

                {/* Bouton de suppression individuelle (affiché uniquement si pas en mode multi-sélection) */}
                {!isMultiSelectMode && isDomainDeletable(domain) && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(domain.id);
                        }}
                        className={`ml-3 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200
                                    ${isSelected ? 'bg-gray-300 hover:bg-gray-400' : 'hover:bg-gray-200'}`}
                        aria-label={`Supprimer le domaine ${displayName}`}
                    >
                        <Trash2 className={`w-4 h-4 ${isSelected ? 'text-gray-600' : 'text-gray-600'}`} />
                    </button>
                )}
            </div>
        );
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 h-full flex flex-col">
            <div className="bg-gray-100 border-b border-gray-200 px-4 py-2 -m-4 mb-4 flex-shrink-0 rounded-t-xl">
                <div className="flex items-center justify-between">
                    <div className="flex items-center text-gray-700">
                        <h2 className="text-sm font-semibold">Domaines d'Expertise</h2>
                    </div>
                    <div className="flex items-center space-x-2">
                        {/* Boutons de sélection multiple */}
                        {hasAnyDomain && (
                            <>
                                <button
                                    onClick={toggleMultiSelectMode}
                                    className={`px-3 py-1 border-2 rounded-lg transition-all duration-200 font-medium shadow-sm hover:shadow-md flex items-center text-sm ${
                                        isMultiSelectMode
                                            ? 'bg-red-100 border-red-300 text-red-700 hover:bg-red-200 hover:border-red-400'
                                            : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200 hover:border-gray-400'
                                    }`}
                                >
                                    {isMultiSelectMode ? (
                                        <>
                                            <X className="w-4 h-4 mr-2" />
                                            Annuler
                                        </>
                                    ) : (
                                        <>
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Sélectionner
                                        </>
                                    )}
                                </button>

                                {/* Bouton de suppression multiple */}
                                {isMultiSelectMode && selectedDomainsForDeletion.size > 0 && (
                                    <button
                                        onClick={handleMultipleDelete}
                                        className="px-3 py-1 bg-red-600 border-2 border-red-600 text-white rounded-lg hover:bg-red-700 hover:border-red-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md flex items-center text-sm"
                                    >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Supprimer ({selectedDomainsForDeletion.size})
                                    </button>
                                )}
                            </>
                        )}

                        <button
                            onClick={handleOpenSkillsModal}
                            className="px-3 py-1 bg-gray-100 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 hover:border-gray-400 transition-all duration-200 font-medium shadow-sm hover:shadow-md flex items-center text-sm"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Ajoutez des domaines
                        </button>
                    </div>
                </div>
            </div>

            {hasAnyDomain ? (
                <div className="flex-1 flex flex-col min-h-0">
                    <div className="flex-1 overflow-y-auto pr-2">
                        <div className="space-y-4">
                            {paginatedDomains.map(renderDomain)}
                        </div>
                    </div>

                    {totalPages > 1 && (
                        <div className="flex-shrink-0 mt-3">
                            <PaginationControls />
                        </div>
                    )}
                </div>
            ) : (
                <div className="text-center py-12 text-gray-500 flex-1 flex flex-col items-center justify-center">
                    <Brain className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium mb-2">Aucun domaine d'expertise défini</p>
                    <p className="text-sm mb-4">
                        Cliquez sur "Ajoutez des domaines" pour commencer à structurer vos compétences
                    </p>
                    <div className="text-xs text-gray-400">
                        Les domaines vous permettent d'organiser vos compétences par thématique
                    </div>
                </div>
            )}
        </div>
    );
};